import React, { createContext, useContext, useState } from 'react';

const translations = {
  pt: {
    nav: {
      about: 'Sobre',
      stack: 'Stack',
      experience: 'Experiência',
      projects: 'Projetos',
      contact: 'Contato',
      downloadCV: 'Baixar CV',
    },
    hero: {
      greeting: '// engenheiro de software',
      name: 'João Pedro\nCarvalho',
      role: 'Backend Engineer',
      tagline: 'Sistemas distribuídos · Microsserviços · Kafka · Node.js · TypeScript',
      contactCta: 'Entrar em contato',
    },
    about: {
      title: 'Sobre',
      paragraphs: [
        'Engenheiro de software com mais de 4 anos de experiência, foco em backend e arquitetura de sistemas. Trabalho com Node.js, TypeScript e MongoDB no desenvolvimento de serviços que precisam ser resilientes, observáveis e escaláveis.',
        'Tenho experiência prática com arquiteturas de microsserviços, mensageria assíncrona com Kafka e implementação de padrões de resiliência como retry com backoff exponencial e circuit breaker. Não só escrevo código — penso em como os sistemas falham, como se recuperam e como evoluem ao longo do tempo.',
        'Antes de migrar para desenvolvimento, atuei em QA. Isso moldou minha visão sobre qualidade: penso em edge cases, comportamento sob carga e contratos de interface antes de começar a implementar.',
      ],
    },
    stack: {
      title: 'Stack',
      categories: [
        { name: 'Backend', items: ['Node.js', 'TypeScript', 'Express', 'NestJS'] },
        { name: 'Mensageria', items: ['Apache Kafka', 'RabbitMQ'] },
        { name: 'Bancos de Dados', items: ['MongoDB', 'PostgreSQL', 'Redis'] },
        { name: 'Frontend', items: ['React', 'Vue.js', 'TypeScript'] },
        { name: 'Observabilidade', items: ['OpenTelemetry', 'Prometheus', 'Grafana', 'Distributed Tracing'] },
        { name: 'Infra & Cloud', items: ['Docker', 'AWS', 'CI/CD', 'Linux'] },
        { name: 'Arquitetura & Padrões', items: ['Microsserviços', 'Event-Driven', 'REST', 'gRPC', 'DDD'] },
      ],
    },
    experience: {
      title: 'Experiência',
      present: 'atual',
      note: '* Substitua os dados abaixo com sua experiência real.',
      jobs: [
        {
          role: 'Desenvolvedor Fullstack Pleno',
          company: '55PBX Telecom e PABX',
          period: '2023 — atual',
          highlights: [
            'Migrou a camada de integração entre serviços de chamadas síncronas para modelo orientado a eventos com Kafka, reduzindo acoplamento e aumentando resiliência em sistema de alta disponibilidade.',
            'Implementou padrões de resiliência (circuit breaker, retry com backoff exponencial, dead letter queue) em pontos críticos da infraestrutura, estabilizando comportamento sob alta carga.',
            'Participou de decisões de arquitetura em novos microsserviços: contratos de API, estratégias de versionamento e instrumentação de observabilidade com distributed tracing.',
          ],
        },
        {
          role: 'QA Engineer / Desenvolvedor',
          company: '55PBX Telecom e PABX',
          period: '2021 — 2023',
          highlights: [
            'Criou e manteve suítes de testes automatizados cobrindo fluxos críticos de negócio, reduzindo incidentes em produção relacionados a regressões.',
            'Contribuiu com desenvolvimento frontend e backend em projetos internos, evoluindo progressivamente para atuação fullstack.',
            'Mapeou riscos técnicos em fluxos críticos de negócio e propôs melhorias no processo de entrega adotadas pelo time.',
          ],
        },
      ],
    },
    projects: {
      title: 'Projetos',
      subtitle: 'Em desenvolvimento · Case studies de arquitetura',
      description: 'Projetos sendo construídos com foco em arquitetura, decisões de design e trade-offs técnicos — não apenas código funcional.',
      badge: 'Em desenvolvimento',
      stackLabel: 'Stack',
      focusLabel: 'Foco',
      decisionsLabel: 'Decisões de arquitetura',
      items: [
        {
          title: 'Order Management System',
          subtitle: 'Microsserviços + Kafka + Saga Pattern',
          description: 'Sistema de gestão de pedidos composto por serviços independentes (order, payment, notification) comunicando-se via Kafka. Implementa o padrão Saga com coreografia para garantir consistência eventual em transações distribuídas.',
          stack: ['Node.js', 'TypeScript', 'Kafka', 'MongoDB', 'Docker', 'Redis'],
          focus: 'Consistência eventual, tolerância a falhas, idempotência',
          decisions: [
            'Saga com coreografia vs. orquestração: escolha e trade-offs',
            'Dead letter queue para mensagens não processadas',
            'Idempotency keys para garantir at-least-once delivery seguro',
          ],
        },
        {
          title: 'Resilience API',
          subtitle: 'Padrões de resiliência enterprise',
          description: 'API backend com implementação nativa de circuit breaker com half-open state, retry com jitter, bulkhead pattern e rate limiting por cliente. Foco em comportamento sob carga e degradação graciosa.',
          stack: ['Node.js', 'TypeScript', 'Redis', 'PostgreSQL', 'Prometheus'],
          focus: 'Resiliência, degradação graciosa, observabilidade',
          decisions: [
            'Circuit breaker vs. retry: análise por tipo e natureza da falha',
            'Jitter no retry para evitar thundering herd problem',
            'Bulkhead para isolamento de falhas entre domínios',
          ],
        },
        {
          title: 'Observability Platform',
          subtitle: 'Stack completa de observabilidade',
          description: 'Stack de observabilidade de ponta a ponta com OpenTelemetry, coleta de métricas com Prometheus, dashboards no Grafana e distributed tracing entre microsserviços. Foco em SLOs e alertas baseados em sinais reais.',
          stack: ['OpenTelemetry', 'Prometheus', 'Grafana', 'Node.js', 'Docker'],
          focus: 'Distributed tracing, SLOs, alertas baseados em sinais',
          decisions: [
            'Logs vs. metrics vs. traces: quando cada um é suficiente',
            'Cardinality management em séries temporais',
            'Definição prática de SLOs por serviço e domínio',
          ],
        },
      ],
    },
    contact: {
      title: 'Contato',
      subtitle: 'Aberto a conversas sobre projetos, oportunidades e colaborações técnicas.',
      email: 'E-mail',
      emailCta: 'Envie um e-mail',
      linkedin: 'LinkedIn',
      linkedinCta: 'Acesse meu LinkedIn',
      whatsapp: 'WhatsApp',
      whatsappCta: 'Envie uma mensagem',
    },
    footer: {
      rights: '© 2025 João Pedro Carvalho.',
    },
  },
  en: {
    nav: {
      about: 'About',
      stack: 'Stack',
      experience: 'Experience',
      projects: 'Projects',
      contact: 'Contact',
      downloadCV: 'Download CV',
    },
    hero: {
      greeting: '// software engineer',
      name: 'João Pedro\nCarvalho',
      role: 'Backend Engineer',
      tagline: 'Distributed systems · Microservices · Kafka · Node.js · TypeScript',
      contactCta: 'Get in touch',
    },
    about: {
      title: 'About',
      paragraphs: [
        'Software engineer with 4+ years of experience, focused on backend and system architecture. I work with Node.js, TypeScript, and MongoDB building services that need to be resilient, observable, and scalable.',
        "I have hands-on experience with microservice architectures, asynchronous messaging with Kafka, and implementing resilience patterns like exponential backoff retry and circuit breakers. I don't just write code — I think about how systems fail, how they recover, and how they evolve over time.",
        'Before moving to development, I worked in QA. That shaped my perspective on quality: I think about edge cases, behavior under load, and interface contracts before I start implementing.',
      ],
    },
    stack: {
      title: 'Stack',
      categories: [
        { name: 'Backend', items: ['Node.js', 'TypeScript', 'Express', 'NestJS'] },
        { name: 'Messaging', items: ['Apache Kafka', 'RabbitMQ'] },
        { name: 'Databases', items: ['MongoDB', 'PostgreSQL', 'Redis'] },
        { name: 'Frontend', items: ['React', 'Vue.js', 'TypeScript'] },
        { name: 'Observability', items: ['OpenTelemetry', 'Prometheus', 'Grafana', 'Distributed Tracing'] },
        { name: 'Infra & Cloud', items: ['Docker', 'AWS', 'CI/CD', 'Linux'] },
        { name: 'Architecture & Patterns', items: ['Microservices', 'Event-Driven', 'REST', 'gRPC', 'DDD'] },
      ],
    },
    experience: {
      title: 'Experience',
      present: 'present',
      note: '* Replace the data below with your actual experience.',
      jobs: [
        {
          role: 'Mid-level Fullstack Developer',
          company: 'Current Company',
          period: '2023 — present',
          highlights: [
            'Migrated the service integration layer from synchronous calls to an event-driven model with Kafka, reducing coupling and increasing resilience in a high-availability system.',
            'Implemented resilience patterns (circuit breaker, exponential backoff retry, dead letter queue) at critical infrastructure points, stabilizing behavior under high-load environments.',
            'Participated in architecture decisions for new microservices: API contracts, versioning strategies, and observability instrumentation with distributed tracing.',
          ],
        },
        {
          role: 'QA Engineer / Developer',
          company: 'Previous Company',
          period: '2021 — 2023',
          highlights: [
            'Created and maintained automated test suites covering critical business flows, reducing production incidents related to regressions.',
            'Contributed to frontend and backend development in internal projects, progressively evolving to fullstack development.',
            'Mapped technical risks in critical business flows and proposed delivery process improvements adopted by the team.',
          ],
        },
      ],
    },
    projects: {
      title: 'Projects',
      subtitle: 'In progress · Architecture case studies',
      description: 'Projects being built with a focus on architecture, design decisions, and technical trade-offs — not just working code.',
      badge: 'In progress',
      stackLabel: 'Stack',
      focusLabel: 'Focus',
      decisionsLabel: 'Architecture decisions',
      items: [
        {
          title: 'Order Management System',
          subtitle: 'Microservices + Kafka + Saga Pattern',
          description: 'Order management system composed of independent services (order, payment, notification) communicating via Kafka. Implements the Saga pattern with choreography to ensure eventual consistency across distributed transactions.',
          stack: ['Node.js', 'TypeScript', 'Kafka', 'MongoDB', 'Docker', 'Redis'],
          focus: 'Eventual consistency, fault tolerance, idempotency',
          decisions: [
            'Saga choreography vs. orchestration: choice and trade-offs',
            'Dead letter queue for unprocessed messages',
            'Idempotency keys to ensure safe at-least-once delivery',
          ],
        },
        {
          title: 'Resilience API',
          subtitle: 'Enterprise resilience patterns',
          description: 'Backend API with native implementation of circuit breaker with half-open state, retry with jitter, bulkhead pattern, and per-client rate limiting. Focus on behavior under load and graceful degradation.',
          stack: ['Node.js', 'TypeScript', 'Redis', 'PostgreSQL', 'Prometheus'],
          focus: 'Resilience, graceful degradation, observability',
          decisions: [
            'Circuit breaker vs. retry: analysis by failure type and nature',
            'Jitter in retry to prevent thundering herd problem',
            'Bulkhead for failure isolation between domains',
          ],
        },
        {
          title: 'Observability Platform',
          subtitle: 'Full observability stack',
          description: 'End-to-end observability stack with OpenTelemetry instrumentation, Prometheus metrics collection, Grafana dashboards, and distributed tracing across microservices. Focus on SLOs and signal-based alerting.',
          stack: ['OpenTelemetry', 'Prometheus', 'Grafana', 'Node.js', 'Docker'],
          focus: 'Distributed tracing, SLOs, signal-based alerting',
          decisions: [
            'Logs vs. metrics vs. traces: when each is sufficient',
            'Cardinality management in time series',
            'Practical SLO definition per service and domain',
          ],
        },
      ],
    },
    contact: {
      title: 'Contact',
      subtitle: 'Open to conversations about projects, opportunities, and technical collaborations.',
      email: 'E-mail',
      emailCta: 'Send an e-mail',
      linkedin: 'LinkedIn',
      linkedinCta: 'View my LinkedIn',
      whatsapp: 'WhatsApp',
      whatsappCta: 'Send a message',
    },
    footer: {
      rights: '© 2025 João Pedro Carvalho.',
    },
  },
};

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState('pt');
  const toggleLang = () => setLang(l => (l === 'pt' ? 'en' : 'pt'));
  const tr = translations[lang];
  return (
    <LanguageContext.Provider value={{ lang, toggleLang, tr }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => useContext(LanguageContext);
